<div class="data-table-container">
  <!-- Search bar and filters -->
  @if (fullConfig().searchable || customFiltersTemplate) {
    <div>
    <div class="flex gap-4 items-center flex-wrap">
      <!-- Search input -->
      @if (fullConfig().searchable) {
      <div class="flex-1 max-w-md mb-4">
        <div class="relative">
          <input 
            [value]="searchTerm()"
            (input)="onSearchChange($any($event.target).value)"
            [placeholder]="fullConfig().searchPlaceholder || 'Buscar...'"
            class="search-input pl-12"
          />
          <lucide-angular
            class="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-light-text-tertiary dark:text-dark-text-tertiary"
            [img]="Search">
          </lucide-angular>
        </div>
      </div>
      }

      <!-- Custom filters projection -->
      @if (customFiltersTemplate) {
        <ng-container *ngTemplateOutlet="customFiltersTemplate"></ng-container>
      }

      <!-- Results count -->
      @if (fullConfig().showResultsCount) {
      <div class="text-light-text-secondary dark:text-dark-text-secondary ml-auto whitespace-nowrap">
        <span class="font-semibold">{{ getResultsCountText() }}</span>
      </div>
      }
    </div>
    </div>
  }

  <!-- Loading state -->
  @if (loading) {
  <div class="flex justify-center items-center py-12 bg-light-surface dark:bg-dark-surface rounded-2xl border border-light-border dark:border-dark-border">
    <div class="flex flex-col items-center gap-3">
      <mat-spinner [diameter]="fullConfig().spinnerDiameter"></mat-spinner>
      @if (fullConfig().loadingMessage) {
      <p class="normal">
        {{ fullConfig().loadingMessage }}
      </p>
      }
    </div>
  </div>
  }

  <!-- Table - Desktop view -->
  @if (!loading) {
  <div class="table-container hidden md:block bg-light-surface dark:bg-dark-surface rounded-2xl border border-light-border dark:border-dark-border overflow-hidden">
    <div 
      class="table-scroll-container"
      [style.maxHeight]="fullConfig().maxHeight"
      [style.overflowY]="fullConfig().maxHeight ? 'auto' : 'visible'"
      [style.overflowX]="'auto'"
      (scroll)="onTableScroll($event)">
      <table 
        mat-table 
        [dataSource]="paginatedData()" 
        matSort
        (matSortChange)="onSortChange($event)"
        class="w-full"
        [class.sticky-header]="fullConfig().stickyHeader">
      
      <caption class="sr-only">{{ tableCaption }}</caption>
      
      <!-- Selection column (checkboxes) -->
      @if (fullConfig().multiSelect) {
      <ng-container matColumnDef="select">
        <th mat-header-cell *matHeaderCellDef class="font-semibold text-light-text dark:text-dark-text bg-light-bg-secondary dark:bg-dark-surface-variant">
          <mat-checkbox
            [checked]="allSelected()"
            [indeterminate]="partialSelection()"
            [matTooltip]="fullConfig().selectAllTooltip || 'Seleccionar todo'"
            (change)="toggleAllSelection()"
            class="checkbox-primary">
          </mat-checkbox>
        </th>
        <td mat-cell *matCellDef="let row">
          <mat-checkbox
            [checked]="isRowSelected(row)"
            [matTooltip]="fullConfig().selectTooltip || 'Seleccionar fila'"
            (change)="toggleRowSelection(row, $event.checked)"
            class="checkbox-primary">
          </mat-checkbox>
        </td>
      </ng-container>
      }
      
      <!-- Dynamic columns -->
      @for (column of visibleColumns(); track column.key) {
      <ng-container [matColumnDef]="column.key">
        <th 
          mat-header-cell 
          *matHeaderCellDef 
          [mat-sort-header]="column.sortable ? column.key : ''"
          [disabled]="!column.sortable"
          class="font-semibold text-light-text dark:text-dark-text bg-light-bg-secondary dark:bg-dark-surface-variant" 
          [style.width]="column.width" 
          [class]="column.cssClass">
          {{ column.header }}
        </th>
        
        <td mat-cell *matCellDef="let row" class="text-light-text-secondary dark:text-dark-text-secondary" [class]="column.cssClass">
          <!-- Text type -->
          @if (column.type === 'text' || column.type === 'number') {
            {{ getCellValue(row, column) }}
          }

          <!-- Date type -->
          @if (column.type === 'date') {
            {{ getCellValue(row, column) | date:'dd/MM/yyyy HH:mm' }}
          }

          <!-- Badge type -->
          @if (column.type === 'badge' && column.badgeConfig) {
            <span 
              class="px-3 py-1 rounded-full text-xs font-medium inline-flex items-center gap-2"
              [ngClass]="getBadgeClass(row, column)">
              @if (column.badgeConfig.getValue(row) && column.badgeConfig.trueIcon) {
              <lucide-angular 
                [img]="column.badgeConfig.trueIcon" 
                class="w-4 h-4">
              </lucide-angular>
              }
              @if (!column.badgeConfig.getValue(row) && column.badgeConfig.falseIcon) {
              <lucide-angular 
                [img]="column.badgeConfig.falseIcon" 
                class="w-4 h-4">
              </lucide-angular>
              }
              {{ getBadgeText(row, column) }}
            </span>
          }

          <!-- Chips type -->
          @if (column.type === 'chips' && column.chipsConfig) {
            <div class="flex flex-wrap gap-2">
              <!-- Custom chips with Tailwind colors -->
              @if (column.chipsConfig.colorProperty && column.chipsConfig.getItems(row).length > 0) {
                @for (item of column.chipsConfig.getItems(row); track $index) {
                <div
                  [ngClass]="item[column.chipsConfig.colorProperty]"
                  class="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-regular transition-all"
                  [class.cursor-pointer]="isChipClickable(column, item, row)"
                  [class.hover:opacity-80]="isChipClickable(column, item, row)"
                  [class.cursor-wait]="column.chipsConfig.isLoading && column.chipsConfig.isLoading(item, row)"
                  (click)="onChipClick($event, column, item, row)">
                  <!-- Spinner when loading -->
                  @if (column.chipsConfig.isLoading && column.chipsConfig.isLoading(item, row)) {
                  <div
                    class="inline-block w-3 h-3 rounded-full border-2 border-current border-t-transparent animate-spin flex-shrink-0">
                  </div>
                  }
                  {{ item[column.chipsConfig.labelProperty] }}
                </div>
                }
              }
              <!-- Default Material chips -->
              @if (!column.chipsConfig.colorProperty && column.chipsConfig.getItems(row).length > 0) {
              <mat-chip-set>
                @for (item of column.chipsConfig.getItems(row); track $index) {
                <mat-chip
                  class="!bg-light-primary/10 dark:!bg-dark-primary/20 !text-light-primary dark:!text-dark-primary text-sm font-medium"
                  [class.cursor-pointer]="isChipClickable(column, item, row)"
                  [class.hover:opacity-80]="isChipClickable(column, item, row)"
                  [class.cursor-wait]="column.chipsConfig.isLoading && column.chipsConfig.isLoading(item, row)"
                  (click)="onChipClick($event, column, item, row)">
                  <!-- Spinner when loading -->
                  @if (column.chipsConfig.isLoading && column.chipsConfig.isLoading(item, row)) {
                  <div
                    class="inline-block w-3 h-3 rounded-full border-2 border-current border-t-transparent animate-spin flex-shrink-0 mr-1">
                  </div>
                  }
                  {{ item[column.chipsConfig.labelProperty] }}
                </mat-chip>
                }
              </mat-chip-set>
              }
              @if (column.chipsConfig.getItems(row).length === 0) {
              <span class="text-light-text-tertiary dark:text-dark-text-tertiary text-sm italic">
                {{ column.chipsConfig.emptyMessage || 'Sin datos' }}
              </span>
              }
            </div>
          }

          <!-- Custom template -->
          @if (column.type === 'custom' && column.customTemplate) {
            <ng-container *ngTemplateOutlet="column.customTemplate; context: { $implicit: row, row: row, column: column }"></ng-container>
          }
        </td>
      </ng-container>
      }

      <!-- Actions column -->
      @if (actions.length > 0) {
      <ng-container matColumnDef="actions">
        <th mat-header-cell *matHeaderCellDef class="font-semibold text-light-text dark:text-dark-text bg-light-bg-secondary dark:bg-dark-surface-variant">
          {{ fullConfig().showActionsColumnHeader ? 'Acciones' : '' }}
        </th>
        <td mat-cell *matCellDef="let row; let i = index">
          <!-- Mode: Compact Actions (dropdown menu) -->
          @if (fullConfig().useCompactActions) {
            @if (getVisibleActionsCount(row) === 1) {
              <!-- Single action: show directly -->
              @if (getFirstVisibleAction(row); as action) {
                <button
                  mat-icon-button 
                  [color]="action.color || 'primary'"
                  [disabled]="isActionDisabled(action, row)"
                  [matTooltip]="action.tooltip || ''"
                  [class]="action.cssClass"
                  (click)="executeAction(action, row, $event)">
                  <lucide-angular [img]="action.lucideIcon" class="w-5 h-5"></lucide-angular>
                </button>
              }
            } @else {
              <!-- Multiple actions: show dropdown menu -->
              <div class="relative">
                <!-- Action menu trigger button -->
                <button
                  mat-icon-button
                  [matTooltip]="'Ver acciones'"
                  (click)="toggleActionMenu($event, i, row)">
                  <lucide-angular [img]="EllipsisVertical" class="w-5 h-5 text-light-primary dark:text-dark-primary"></lucide-angular>
                </button>
              </div>
            }
          } @else {
            <!-- Mode: Standard Actions (all buttons visible) -->
            <div class="flex gap-1">
              @for (action of actions; track action.tooltip) {
              <button
                [hidden]="!isActionVisible(action, row)"
                mat-icon-button 
                [color]="action.color || 'primary'"
                [disabled]="isActionDisabled(action, row)"
                [matTooltip]="action.tooltip || ''"
                [class]="action.cssClass"
                (click)="executeAction(action, row, $event)">
                <lucide-angular [img]="action.lucideIcon" class="w-5 h-5"></lucide-angular>
              </button>
              }
            </div>
          }
       </td>
      </ng-container>
      }

      <!-- Table rows -->
      <tr mat-header-row *matHeaderRowDef="displayedColumns()"></tr>
      <tr 
        mat-row 
        *matRowDef="let row; columns: displayedColumns();"
        class="hover:bg-light-bg-tertiary dark:hover:bg-dark-surface-variant transition-colors duration-200 cursor-pointer border-b border-light-border dark:border-dark-border"
        (click)="onRowClick(row)">
      </tr>

      <!-- Empty state -->
      <tr *matNoDataRow>
        <td [attr.colspan]="displayedColumns().length" class="text-center py-16">
          <!-- Custom empty state template -->
          @if (emptyStateTemplate) {
            <ng-container *ngTemplateOutlet="emptyStateTemplate"></ng-container>
          } @else {
            <div class="flex flex-col items-center gap-4">
              <div class="w-20 h-20 bg-light-bg-secondary dark:bg-dark-surface-variant rounded-3xl flex items-center justify-center">
                @switch (fullConfig().emptyLucideIcon ? 'lucide' : fullConfig().emptyIcon) {
                  @case ('lucide') {
                    <lucide-angular [img]="fullConfig().emptyLucideIcon" class="w-10 h-10 text-light-text-tertiary dark:text-dark-text-tertiary"></lucide-angular>
                  }
                  @case ('search') {
                    <lucide-icon [img]="Search" class="w-10 h-10 text-light-text-tertiary dark:text-dark-text-tertiary"></lucide-icon>
                  }
                  @default {
                    <lucide-angular [img]="Inbox" class="w-10 h-10 text-light-text-tertiary dark:text-dark-text-tertiary"></lucide-angular>
                  }
                }
              </div>
              <div>
                <h3 class="text-xl font-semibold text-light-text dark:text-dark-text mb-2">
                  {{ fullConfig().emptyMessage }}
                </h3>
                @if (fullConfig().emptySubMessage) {
                <p class="normal">
                  {{ fullConfig().emptySubMessage }}
                </p>
                }
              </div>
            </div>
          }
        </td>
      </tr>
    </table>
    
    <!-- Loading more indicator -->
    @if (loadingMore) {
    <div class="flex justify-center items-center py-4 border-t border-light-border dark:border-dark-border">
      <mat-spinner diameter="24"></mat-spinner>
      <span class="ml-3 text-sm text-light-text-secondary dark:text-dark-text-secondary">Cargando más...</span>
    </div>
    }
    </div>

    <!-- Action dropdown menu - Rendered outside table to avoid overflow issues -->
    @if (openMenuRowIndex() !== null && fullConfig().useCompactActions) {
      <!-- Backdrop to close menu when clicking outside -->
      <div 
        class="fixed inset-0 z-[60]" 
        (click)="closeActionMenu()">
      </div>
      
      <!-- Menu positioned fixed -->
      <div 
        class="fixed z-[70]"
        [style.top.px]="menuPosition.top"
        [style.left.px]="menuPosition.left">
        <app-button-display
          [items]="menuItems()"
          [isCompact]="true"
          containerClass="action-dropdown-menu compact"
          iconClass="w-5 h-5 text-light-primary dark:text-dark-primary"
        ></app-button-display>
      </div>
    }

    <!-- Paginator -->
    @if (fullConfig().pageable) {
    <mat-paginator
      [length]="getTotalResults()"
      [pageSize]="pageSize()"
      [pageIndex]="pageIndex()"
      [pageSizeOptions]="fullConfig().pageSizeOptions || []"
      [showFirstLastButtons]="fullConfig().showFirstLastButtons || false"
      (page)="onPageChange($event)"
      class="border-t border-light-border dark:border-dark-border bg-light-bg-secondary dark:bg-dark-surface-variant">
    </mat-paginator>
    }
  </div>
  }

  <!-- Mobile Cards View -->
  @if (!loading) {
  <div 
    class="mobile-cards-container md:hidden space-y-4"
    [style.maxHeight]="fullConfig().maxHeight"
    [style.overflowY]="fullConfig().maxHeight ? 'auto' : 'visible'"
    [style.overflowX]="'hidden'"
    (scroll)="onTableScroll($event)">
    <!-- Empty state for mobile -->
    @if (paginatedData().length === 0) {
    <div class="bg-light-surface dark:bg-dark-surface rounded-2xl border border-light-border dark:border-dark-border p-8">
      @if (emptyStateTemplate) {
        <ng-container *ngTemplateOutlet="emptyStateTemplate"></ng-container>
      } @else {
        <div class="flex flex-col items-center gap-4">
          <div class="w-20 h-20 bg-light-bg-secondary dark:bg-dark-surface-variant rounded-3xl flex items-center justify-center">
            @switch (fullConfig().emptyLucideIcon ? 'lucide' : fullConfig().emptyIcon) {
              @case ('lucide') {
                <lucide-angular [img]="fullConfig().emptyLucideIcon" class="w-10 h-10 text-light-text-tertiary dark:text-dark-text-tertiary"></lucide-angular>
              }
              @case ('search') {
                <lucide-icon [img]="Search" class="w-10 h-10 text-light-text-tertiary dark:text-dark-text-tertiary"></lucide-icon>
              }
              @default {
                <lucide-angular [img]="Inbox" class="w-10 h-10 text-light-text-tertiary dark:text-dark-text-tertiary"></lucide-angular>
              }
            }
          </div>
          <div class="text-center">
            <h3 class="text-xl font-semibold text-light-text dark:text-dark-text mb-2">
              {{ fullConfig().emptyMessage }}
            </h3>
            @if (fullConfig().emptySubMessage) {
            <p class="normal">
              {{ fullConfig().emptySubMessage }}
            </p>
            }
          </div>
        </div>
      }
    </div>
    }

    <!-- Cards -->
    @for (row of paginatedData(); track $index) {
    <div
      class="mobile-card bg-light-surface dark:bg-dark-surface rounded-2xl border border-light-border dark:border-dark-border p-4 shadow-soft hover:shadow-medium transition-shadow duration-200"
      (click)="onRowClick(row)">
      
      <!-- Primera fila: Checkbox (si está habilitado), ID y Acciones -->
      <div class="flex justify-between items-center mb-3 pb-3 border-b border-light-border dark:border-dark-border">
        <div class="flex items-center gap-3">
          <!-- Checkbox para selección múltiple -->
          @if (fullConfig().multiSelect) {
          <mat-checkbox
            [checked]="isRowSelected(row)"
            [matTooltip]="fullConfig().selectTooltip || 'Seleccionar fila'"
            (change)="onCheckboxChange($event, row)"
            (click)="$event.stopPropagation()"
            class="checkbox-primary">
          </mat-checkbox>
          }
          
          <!-- ID (primera columna visible que no sea actions) -->
          @if (visibleColumns().length > 0) {
          <div class="flex items-center gap-2">
            <span class="text-xs font-medium text-light-text-tertiary dark:text-dark-text-tertiary uppercase">
              {{ visibleColumns()[0].header }}
            </span>
            @switch (visibleColumns()[0].type) {
              @case ('custom') {
                <ng-container *ngTemplateOutlet="visibleColumns()[0].customTemplate ?? null; context: { $implicit: row, row: row, column: visibleColumns()[0] }"></ng-container>
              }
              @default {
                <span class="text-sm font-semibold text-light-text dark:text-dark-text">
                  {{ getCellValue(row, visibleColumns()[0]) }}
                </span>
              }
            }
          </div>
          }
        </div>

        <!-- Acciones -->
        @if (actions.length > 0) {
        <div class="flex gap-1">
          @for (action of actions; track action.tooltip) {
          <button
            [hidden]="!isActionVisible(action, row)"
            mat-icon-button 
            [color]="action.color || 'primary'"
            [disabled]="isActionDisabled(action, row)"
            [matTooltip]="action.tooltip || ''"
            [class]="action.cssClass"
            (click)="executeAction(action, row, $event)">
            <lucide-angular [img]="action.lucideIcon" class="w-5 h-5"></lucide-angular>
          </button>
          }
        </div>
        }
      </div>

      <!-- Resto de columnas -->
      <div class="space-y-2">
        @for (column of visibleColumns(); track column.key; let i = $index) {
        <div [class.hidden]="i === 0">
          <div class="flex flex-col gap-1">
            <span class="text-xs font-medium text-light-text-secondary dark:text-dark-text-secondary">
              {{ column.header }}
            </span>
            
            <!-- Text type -->
            @if (column.type === 'text' || column.type === 'number') {
            <span class="text-sm text-light-text dark:text-dark-text">
              {{ getCellValue(row, column) }}
            </span>
            }

            <!-- Date type -->
            @if (column.type === 'date') {
            <span class="text-sm text-light-text dark:text-dark-text">
              {{ getCellValue(row, column) | date:'dd/MM/yyyy HH:mm' }}
            </span>
            }

            <!-- Badge type -->
            @if (column.type === 'badge' && column.badgeConfig) {
            <div>
              <span 
                class="inline-block px-3 py-1 rounded-full text-xs font-medium inline-flex items-center gap-2"
                [ngClass]="getBadgeClass(row, column)">
                @if (column.badgeConfig.getValue(row) && column.badgeConfig.trueIcon) {
                <lucide-angular 
                  [img]="column.badgeConfig.trueIcon" 
                  class="w-5 h-5">
                </lucide-angular>
                }
                @if (!column.badgeConfig.getValue(row) && column.badgeConfig.falseIcon) {
                <lucide-angular 
                  [img]="column.badgeConfig.falseIcon" 
                  class="w-5 h-5">
                </lucide-angular>
                }
                {{ getBadgeText(row, column) }}
              </span>
            </div>
            }

            <!-- Chips type -->
            @if (column.type === 'chips' && column.chipsConfig) {
            <div class="flex flex-wrap gap-2">
              <!-- Custom chips with Tailwind colors -->
              @if (column.chipsConfig.colorProperty && column.chipsConfig.getItems(row).length > 0) {
                @for (item of column.chipsConfig.getItems(row); track $index) {
                <div
                  [ngClass]="item[column.chipsConfig.colorProperty]"
                  class="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-medium transition-all"
                  [class.cursor-pointer]="isChipClickable(column, item, row)"
                  [class.hover:opacity-80]="isChipClickable(column, item, row)"
                  [class.cursor-wait]="column.chipsConfig.isLoading && column.chipsConfig.isLoading(item, row)"
                  (click)="onChipClick($event, column, item, row)">
                  {{ item[column.chipsConfig.labelProperty] }}
                  <!-- Spinner when loading -->
                  @if (column.chipsConfig.isLoading && column.chipsConfig.isLoading(item, row)) {
                  <div
                    class="inline-block w-3 h-3 rounded-full border-2 border-current border-t-transparent animate-spin flex-shrink-0">
                  </div>
                  }
                </div>
                }
              }
              <!-- Default Material chips -->
              @if (!column.chipsConfig.colorProperty && column.chipsConfig.getItems(row).length > 0) {
              <mat-chip-set>
                @for (item of column.chipsConfig.getItems(row); track $index) {
                <mat-chip
                  class="!bg-light-primary/10 dark:!bg-dark-primary/20 !text-light-primary dark:!text-dark-primary"
                  [class.cursor-pointer]="isChipClickable(column, item, row)"
                  [class.hover:opacity-80]="isChipClickable(column, item, row)"
                  [class.cursor-wait]="column.chipsConfig.isLoading && column.chipsConfig.isLoading(item, row)"
                  (click)="onChipClick($event, column, item, row)">
                  {{ item[column.chipsConfig.labelProperty] }}
                  <!-- Spinner when loading -->
                  @if (column.chipsConfig.isLoading && column.chipsConfig.isLoading(item, row)) {
                  <div
                    class="inline-block w-3 h-3 rounded-full border-2 border-current border-t-transparent animate-spin flex-shrink-0 mr-1">
                  </div>
                  }
                </mat-chip>
                }
              </mat-chip-set>
              }
              @if (column.chipsConfig.getItems(row).length === 0) {
              <span class="text-light-text-tertiary dark:text-dark-text-tertiary text-sm italic">
                {{ column.chipsConfig.emptyMessage || 'Sin datos' }}
              </span>
              }
            </div>
            }

            <!-- Custom template -->
            @if (column.type === 'custom' && column.customTemplate) {
            <div>
              <ng-container *ngTemplateOutlet="column.customTemplate; context: { $implicit: row, row: row, column: column }"></ng-container>
            </div>
            }
          </div>
        </div>
        }
      </div>
    </div>
    }

    <!-- Loading more indicator - Mobile -->
    @if (loadingMore) {
    <div class="flex justify-center items-center py-4 bg-light-surface dark:bg-dark-surface rounded-2xl border border-light-border dark:border-dark-border">
      <mat-spinner diameter="24"></mat-spinner>
      <span class="ml-3 text-sm text-light-text-secondary dark:text-dark-text-secondary">Cargando más...</span>
    </div>
    }

    <!-- Mobile Paginator -->
    @if (fullConfig().pageable) {
    <mat-paginator
      [length]="getTotalResults()"
      [pageSize]="pageSize()"
      [pageIndex]="pageIndex()"
      [pageSizeOptions]="fullConfig().pageSizeOptions || []"
      [showFirstLastButtons]="fullConfig().showFirstLastButtons || false"
      (page)="onPageChange($event)"
      class="bg-light-surface dark:bg-dark-surface rounded-2xl border border-light-border dark:border-dark-border">
    </mat-paginator>
    }
  </div>
  }
</div>
